import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';
// Removed Sentry service - using Elasticsearch for performance monitoring

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  id: string;
  url: string;
}

interface PerformanceConfig {
  enableConsoleLogging?: boolean;
  enableAnalytics?: boolean;
  enableSentry?: boolean;
  customHandler?: (metric: PerformanceMetric) => void;
}

class PerformanceService {
  private config: PerformanceConfig = {
    enableConsoleLogging: false,
    enableAnalytics: true,
    enableSentry: false, // Disabled Sentry integration
  };
  private metrics: PerformanceMetric[] = [];

  initialize(config?: PerformanceConfig): void {
    this.config = { ...this.config, ...config };

    // Initialize Web Vitals monitoring
    onCLS(this.handleMetric.bind(this));
    onFCP(this.handleMetric.bind(this));
    onLCP(this.handleMetric.bind(this));
    onTTFB(this.handleMetric.bind(this));
    // Use INP (Interaction to Next Paint) instead of FID in web-vitals v3+
    onINP(this.handleMetric.bind(this));

    // Monitor custom performance metrics
    this.observeNavigationTiming();
    this.observeResourceTiming();
  }

  private handleMetric(metric: Metric): void {
    const performanceMetric: PerformanceMetric = {
      name: metric.name,
      value: metric.value,
      rating: metric.rating,
      timestamp: Date.now(),
      id: metric.id,
      url: window.location.href,
    };

    this.metrics.push(performanceMetric);

    if (this.config.enableConsoleLogging && !import.meta.env.PROD) {
      console.log('Performance Metric:', performanceMetric);
    }

    // Sentry performance tracking disabled - using Elasticsearch instead

    if (this.config.customHandler) {
      this.config.customHandler(performanceMetric);
    }

    // Trigger analytics if enabled
    if (this.config.enableAnalytics) {
      this.sendToAnalytics(performanceMetric);
    }
  }

  private observeNavigationTiming(): void {
    if ('performance' in window && 'getEntriesByType' in performance) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            
            // Track custom navigation metrics
            this.trackCustomMetric('dom-content-loaded', navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart);
            this.trackCustomMetric('load-complete', navEntry.loadEventEnd - navEntry.loadEventStart);
            this.trackCustomMetric('dns-lookup', navEntry.domainLookupEnd - navEntry.domainLookupStart);
            this.trackCustomMetric('tcp-connect', navEntry.connectEnd - navEntry.connectStart);
          }
        });
      });

      observer.observe({ entryTypes: ['navigation'] });
    }
  }

  private observeResourceTiming(): void {
    if ('performance' in window && 'getEntriesByType' in performance) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.entryType === 'resource') {
            const resourceEntry = entry as PerformanceResourceTiming;
            
            // Track slow resources (>1s load time)
            if (resourceEntry.duration > 1000) {
              this.trackCustomMetric(
                'slow-resource',
                resourceEntry.duration,
                { resourceUrl: resourceEntry.name }
              );
            }
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });
    }
  }

  trackCustomMetric(name: string, value: number, metadata?: Record<string, any>): void {
    const metric: PerformanceMetric = {
      name,
      value,
      rating: this.getMetricRating(name, value),
      timestamp: Date.now(),
      id: this.generateId(),
      url: window.location.href,
    };

    this.metrics.push(metric);

    if (this.config.enableConsoleLogging && !import.meta.env.PROD) {
      console.log('Custom Performance Metric:', metric, metadata);
    }

    // Sentry custom metric tracking disabled - using Elasticsearch instead

    if (this.config.customHandler) {
      this.config.customHandler(metric);
    }
  }

  private getMetricRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    // Define thresholds for different metrics
    const thresholds: Record<string, [number, number]> = {
      'dom-content-loaded': [1000, 2000],
      'load-complete': [2000, 4000],
      'dns-lookup': [100, 300],
      'tcp-connect': [100, 300],
      'slow-resource': [1000, 3000],
    };

    const [goodThreshold, poorThreshold] = thresholds[name] || [1000, 2500];

    if (value <= goodThreshold) return 'good';
    if (value <= poorThreshold) return 'needs-improvement';
    return 'poor';
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  private sendToAnalytics(metric: PerformanceMetric): void {
    // This will be implemented with the analytics service
    window.dispatchEvent(new CustomEvent('performance-metric', { detail: metric }));
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter(metric => metric.name === name);
  }

  clearMetrics(): void {
    this.metrics = [];
  }

  getPerformanceSummary(): Record<string, { count: number; average: number; rating: Record<string, number> }> {
    const summary: Record<string, { count: number; average: number; rating: Record<string, number> }> = {};

    this.metrics.forEach(metric => {
      if (!summary[metric.name]) {
        summary[metric.name] = {
          count: 0,
          average: 0,
          rating: { good: 0, 'needs-improvement': 0, poor: 0 }
        };
      }

      summary[metric.name].count++;
      summary[metric.name].average = (summary[metric.name].average * (summary[metric.name].count - 1) + metric.value) / summary[metric.name].count;
      summary[metric.name].rating[metric.rating]++;
    });

    return summary;
  }
}

export const performanceService = new PerformanceService();